import { isCarePlanCompleted } from './resolvers.mjs';
import { HEALTHCARE_SERVICE_CODESYSTEMS, OPERATIONAL_PROGRAM_SYSTEM, PROGRAM_SELECTION_OBSERVATION_SYSTEM, SELECTED_NEEDS_COMPONENT_SYSTEM, SELECTED_NEEDS_COMPONENT_CODE } from './constants.mjs';

/** Validates care plan can be completed. @agentic @benchmark/core */ function validateCarePlanCompletion(carePlan, force = false) {
    const errors = [];
    const warnings = [];
    // Check care plan status
    if (carePlan.status === "completed") {
        errors.push("Care plan is already completed");
    }
    if (carePlan.status !== "active" && carePlan.status !== "on-hold") {
        errors.push(`Care plan must be active or on-hold to complete. Current status: ${carePlan.status}`);
    }
    // Check if all activities are completed
    if (carePlan.activity && carePlan.activity.length > 0) {
        const incompleteActivities = carePlan.activity.filter((activity)=>activity.detail?.status !== "completed");
        if (incompleteActivities.length > 0 && !force) {
            warnings.push(`Not all activities are completed. ${incompleteActivities.length} activity/activities remain incomplete. Use force=true to complete anyway.`);
        }
    }
    return {
        isValid: errors.length === 0,
        errors,
        warnings
    };
}
/** Validates care plan can be cancelled. @agentic @benchmark/core */ function validateCarePlanCancellation(carePlan) {
    const errors = [];
    const warnings = [];
    if (isCarePlanCompleted(carePlan)) {
        errors.push("Cannot cancel a completed care plan");
    }
    if (carePlan.status === "revoked") {
        errors.push("Care plan is already cancelled");
    }
    if (carePlan.status !== "draft" && carePlan.status !== "active" && carePlan.status !== "on-hold") {
        errors.push(`Care plan cannot be cancelled from ${carePlan.status} status. Only draft, active, or on-hold care plans can be cancelled.`);
    }
    return {
        isValid: errors.length === 0,
        errors,
        warnings
    };
}
/** Validates care plan type transition. @agentic @benchmark/core */ function validateCarePlanTypeTransition(_newCarePlanType, _existingCarePlans) {
    // Intake prerequisites removed - keep structure for future business rules
    return {
        isValid: true,
        errors: [],
        warnings: []
    };
}
/** Validates CoCM care plan can be created. @agentic @benchmark/core */ function validateCoCMCarePlanCreation(_existingCarePlans) {
    return {
        isValid: true,
        errors: [],
        warnings: []
    };
}
/** Validates triage care plan can be created. @agentic @benchmark/core */ function validateTriageCarePlanCreation(_existingCarePlans) {
    return {
        isValid: true,
        errors: [],
        warnings: []
    };
}
/** Validates care plan can be activated. @deprecated Care plans are now created in active status. @agentic @benchmark/core */ function validateCarePlanActivation(carePlan, _existingCarePlans) {
    const errors = [];
    const warnings = [];
    // Check care plan status
    if (carePlan.status !== "draft") {
        errors.push(`Care plan must be in draft status to activate. Current status: ${carePlan.status}`);
    }
    return {
        isValid: errors.length === 0,
        errors,
        warnings
    };
}

/** Dimension-specific CodeSystem prefixes. @agentic @benchmark/core */ const DIMENSION_SPECIFIC_PREFIXES = [
    "https://benchmarkhealth.com/fhir/CodeSystem/service-eligibility-",
    "https://benchmarkhealth.com/fhir/CodeSystem/service-characteristic-",
    "https://benchmarkhealth.com/fhir/CodeSystem/service-program-",
    "https://benchmarkhealth.com/fhir/CodeSystem/service-referralMethod-",
    "https://benchmarkhealth.com/fhir/CodeSystem/service-specialty-"
];
/** Checks if CodeableConcept uses the correct CodeSystem. @agentic @benchmark/core */ function hasCodeSystem(concept, expectedSystem) {
    if (!concept?.coding || concept.coding.length === 0) {
        return false;
    }
    return concept.coding.some((coding)=>coding.system === expectedSystem);
}
/** Checks if CodeableConcept has dimension + value coding pattern. @agentic @benchmark/core */ function hasDimensionValueCodingPattern(concept) {
    if (!concept?.coding || concept.coding.length < 2) {
        return false;
    }
    // Check if Coding[0] is from dimension CodeSystem
    const firstCoding = concept.coding[0];
    if (!firstCoding?.system || !firstCoding?.code) {
        return false;
    }
    // Check if it's a dimension CodeSystem
    const isDimensionCoding = firstCoding.system === HEALTHCARE_SERVICE_CODESYSTEMS.SERVICE_ELIGIBILITY_DIMENSION || firstCoding.system === HEALTHCARE_SERVICE_CODESYSTEMS.SERVICE_PROGRAM_DIMENSION || firstCoding.system === HEALTHCARE_SERVICE_CODESYSTEMS.SERVICE_REFERRAL_METHOD_DIMENSION || firstCoding.system === HEALTHCARE_SERVICE_CODESYSTEMS.SERVICE_SPECIALTY_DIMENSION;
    if (!isDimensionCoding) {
        return false;
    }
    // Check if Coding[1] exists and is from a dimension-specific CodeSystem
    const secondCoding = concept.coding[1];
    if (!secondCoding?.system || !secondCoding?.code) {
        return false;
    }
    // Check if second coding is from a dimension-specific CodeSystem
    // TypeScript guard: system is checked above, but TypeScript doesn't narrow properly
    const system = secondCoding.system;
    if (!system) {
        return false;
    }
    return DIMENSION_SPECIFIC_PREFIXES.some((prefix)=>system.startsWith(prefix));
}
/** Checks if characteristic follows valid pattern (dimension+value or dimension-specific CodeSystem). @agentic @benchmark/core */ function hasValidCharacteristicPattern(concept) {
    if (!concept?.coding || concept.coding.length === 0) {
        return false;
    }
    const firstCoding = concept.coding[0];
    if (!firstCoding?.system || !firstCoding?.code) {
        return false;
    }
    // Pattern 1: dimension + value coding
    if (firstCoding.system === HEALTHCARE_SERVICE_CODESYSTEMS.SERVICE_CHARACTERISTIC_DIMENSION && concept.coding.length >= 2) {
        const secondCoding = concept.coding[1];
        if (!secondCoding?.system) {
            return false;
        }
        if (secondCoding.system.startsWith("https://benchmarkhealth.com/fhir/CodeSystem/service-characteristic-")) {
            return true;
        }
    }
    // Pattern 2: dimension-specific CodeSystem only
    if (firstCoding.system.startsWith("https://benchmarkhealth.com/fhir/CodeSystem/service-characteristic-")) {
        return true;
    }
    return false;
}
/** Checks if Location reference is valid. @agentic @benchmark/core */ function isValidLocationReference(reference) {
    if (!reference?.reference) {
        return false;
    }
    // Valid format: "Location/id" or "Location/id|version"
    return /^Location\/[^|]+/.test(reference.reference);
}
/** Checks if HealthcareService conforms to new data model specification. @agentic @benchmark/core */ function checkHealthcareServiceConformance(service) {
    const issues = [];
    // Check category: exactly one required
    if (!service.category || service.category.length === 0) {
        issues.push("Must have exactly one category");
    } else if (service.category.length !== 1) {
        issues.push("Must have exactly one category");
    } else {
        const category = service.category[0];
        if (!hasCodeSystem(category, HEALTHCARE_SERVICE_CODESYSTEMS.SERVICE_CATEGORY)) {
            issues.push("Category must use service-category CodeSystem");
        }
    }
    // Check type: exactly one required
    if (!service.type || service.type.length === 0) {
        issues.push("Must have exactly one type");
    } else if (service.type.length !== 1) {
        issues.push("Must have exactly one type");
    } else {
        const type = service.type[0];
        if (!hasCodeSystem(type, HEALTHCARE_SERVICE_CODESYSTEMS.SERVICE_TYPE)) {
            issues.push("Type must use service-type CodeSystem");
        }
    }
    // Check specialty: zero or many allowed
    // Supports both old flat format (deprecated) and new dimension + value pattern
    if (service.specialty && service.specialty.length > 0) {
        service.specialty.forEach((specialty, index)=>{
            // Check for new dimension + value pattern
            const hasDimensionPattern = hasDimensionValueCodingPattern(specialty);
            // Check for old flat format (deprecated but still valid for backward compatibility)
            const hasOldFormat = hasCodeSystem(specialty, HEALTHCARE_SERVICE_CODESYSTEMS.SERVICE_SPECIALTY);
            // Check for dimension-specific CodeSystem (treatment-modality or treatment-condition)
            const hasDimensionSpecificCodeSystem = specialty.coding?.some((coding)=>coding.system?.startsWith("https://benchmarkhealth.com/fhir/CodeSystem/service-specialty-"));
            if (!hasDimensionPattern && !hasOldFormat && !hasDimensionSpecificCodeSystem) {
                issues.push(`Specialty[${index}] should use dimension + value pattern (service-specialty-dimension) or dimension-specific CodeSystem. Old flat format is deprecated.`);
            }
        });
    }
    // Check eligibility: all items must have dimension + value coding
    if (service.eligibility && service.eligibility.length > 0) {
        service.eligibility.forEach((elig, index)=>{
            // FHIR R4 uses 'code' property, but Notion spec shows 'codeableConcept'
            // Check both for compatibility
            const codeableConcept = elig.codeableConcept || elig.code;
            if (!hasDimensionValueCodingPattern(codeableConcept)) {
                issues.push(`Eligibility[${index}] missing dimension or value coding`);
            }
        });
    }
    // Check characteristic: items can have dimension+value OR dimension-specific CodeSystem
    if (service.characteristic && service.characteristic.length > 0) {
        service.characteristic.forEach((char, index)=>{
            if (!hasValidCharacteristicPattern(char)) {
                issues.push(`Characteristic[${index}] invalid pattern`);
            }
        });
    }
    // Check program: all items must have dimension + value coding
    if (service.program && service.program.length > 0) {
        service.program.forEach((prog, index)=>{
            if (!hasDimensionValueCodingPattern(prog)) {
                issues.push(`Program[${index}] missing dimension or value coding`);
            }
        });
    }
    // Check referralMethod: all items must have dimension + value coding
    if (service.referralMethod && service.referralMethod.length > 0) {
        service.referralMethod.forEach((ref, index)=>{
            if (!hasDimensionValueCodingPattern(ref)) {
                issues.push(`ReferralMethod[${index}] missing dimension or value coding`);
            }
        });
    }
    // Check serviceProvisionCode: proper structure if present
    if (service.serviceProvisionCode && service.serviceProvisionCode.length > 0) {
        service.serviceProvisionCode.forEach((code, index)=>{
            if (!hasCodeSystem(code, HEALTHCARE_SERVICE_CODESYSTEMS.SERVICE_PROVISION)) {
                issues.push(`ServiceProvisionCode[${index}] must use service-provision CodeSystem`);
            }
        });
    }
    // Check location: valid references if present
    if (service.location && service.location.length > 0) {
        service.location.forEach((loc, index)=>{
            if (!isValidLocationReference(loc)) {
                issues.push(`Location[${index}] invalid reference`);
            }
        });
    }
    // Check coverageArea: valid Location references if present.
    // Cardinality stays optional (0..*); we only assert reference shape so
    // dangling or malformed references surface in the conformance banner
    // the same way invalid `location` references do.
    if (service.coverageArea && service.coverageArea.length > 0) {
        service.coverageArea.forEach((ref, index)=>{
            if (!isValidLocationReference(ref)) {
                issues.push(`CoverageArea[${index}] invalid reference`);
            }
        });
    }
    return {
        isConformant: issues.length === 0,
        issues,
        details: {
            hasCategory: !!service.category && service.category.length > 0,
            categoryCount: service.category?.length || 0,
            hasType: !!service.type && service.type.length > 0,
            typeCount: service.type?.length || 0,
            eligibilityCount: service.eligibility?.length || 0,
            characteristicCount: service.characteristic?.length || 0,
            programCount: service.program?.length || 0,
            referralMethodCount: service.referralMethod?.length || 0,
            serviceProvisionCodeCount: service.serviceProvisionCode?.length || 0,
            locationCount: service.location?.length || 0,
            coverageAreaCount: service.coverageArea?.length || 0
        }
    };
}
/** Maps conformance issues to form field names. @agentic @benchmark/core */ function mapConformanceIssuesToFields(conformanceResult, service) {
    const fieldIssues = {};
    // Map each issue to a form field
    conformanceResult.issues.forEach((issue)=>{
        if (issue.includes("category")) {
            fieldIssues.category = fieldIssues.category || [];
            fieldIssues.category.push(issue);
        }
        if (issue.includes("type")) {
            fieldIssues.type = fieldIssues.type || [];
            fieldIssues.type.push(issue);
        }
        if (issue.includes("Specialty")) {
            fieldIssues.specialty = fieldIssues.specialty || [];
            fieldIssues.specialty.push(issue);
        }
        if (issue.includes("Eligibility")) {
            fieldIssues.eligibilityCriteria = fieldIssues.eligibilityCriteria || [];
            fieldIssues.eligibilityCriteria.push(issue);
        }
        if (issue.includes("Program")) {
            fieldIssues.program = fieldIssues.program || [];
            fieldIssues.program.push(issue);
        }
        if (issue.includes("ReferralMethod")) {
            fieldIssues.referralMethod = fieldIssues.referralMethod || [];
            fieldIssues.referralMethod.push(issue);
        }
        if (issue.includes("ServiceProvisionCode")) {
            fieldIssues.serviceProvisionCode = fieldIssues.serviceProvisionCode || [];
            fieldIssues.serviceProvisionCode.push(issue);
        }
        if (issue.includes("CoverageArea")) {
            fieldIssues.coverageArea = fieldIssues.coverageArea || [];
            fieldIssues.coverageArea.push(issue);
        }
        // Note: "CoverageArea" does not contain "Location", so coverageArea
        // issues never fall through to the location field.
        if (issue.includes("Location")) {
            fieldIssues.location = fieldIssues.location || [];
            fieldIssues.location.push(issue);
        }
    });
    // Check for careModality migration issue even if no characteristic issues
    const careModalityTag = service.meta?.tag?.find((tag)=>tag.system === "http://benchmarkhealth.com/fhir/care-modality");
    if (careModalityTag) {
        const hasVisitModeCharacteristic = service.characteristic?.some((char)=>char.coding?.some((coding)=>coding.system === HEALTHCARE_SERVICE_CODESYSTEMS.SERVICE_CHARACTERISTIC_VISIT_MODE));
        if (!hasVisitModeCharacteristic) {
            fieldIssues.careModality = fieldIssues.careModality || [];
            fieldIssues.careModality.push("careModality should be migrated to characteristic field with visitMode dimension");
        }
    }
    return fieldIssues;
}

/**
 * Resolve the selected program code from a Program Selection Observation.
 * @agentic @benchmark/core
 */ const resolveSelectedProgram = (observation)=>observation.valueCodeableConcept?.coding?.find((c)=>c.system === OPERATIONAL_PROGRAM_SYSTEM)?.code;
/**
 * Resolve selected needs from the Observation component array.
 * @agentic @benchmark/core
 */ const resolveSelectedNeeds = (observation)=>{
    const component = observation.component?.find((c)=>c.code?.coding?.some((coding)=>coding.system === SELECTED_NEEDS_COMPONENT_SYSTEM && coding.code === SELECTED_NEEDS_COMPONENT_CODE.code));
    if (!component?.valueString) return [];
    return component.valueString.split(",").filter(Boolean);
};
/**
 * Resolve reason codes from the Observation component array.
 * @agentic @benchmark/core
 */ const resolveReasonCodes = (observation)=>{
    const component = observation.component?.find((c)=>c.code?.coding?.some((coding)=>coding.system === PROGRAM_SELECTION_OBSERVATION_SYSTEM && coding.code === "selection-reason"));
    if (!component?.valueString) return [];
    return component.valueString.split(",").filter(Boolean);
};
/**
 * Validate whether the intake completion gate is satisfied.
 *
 * Checks:
 * 1. Program Selection Observation exists
 * 2. Observation has a valid selected program (valueCodeableConcept)
 * 3. Observation has at least one reason code
 * 4. If triage: selected needs are non-empty
 *
 * @param observation - The Program Selection Observation (or undefined if none found)
 * @returns Validation result with structured errors
 * @agentic @benchmark/core
 */ function validateIntakeCompletionGate(observation) {
    const errors = [];
    if (!observation) {
        errors.push("Program selection is required before completing intake. Please select a program (Triage or CoCM).");
        return {
            isValid: false,
            errors
        };
    }
    const selectedProgram = resolveSelectedProgram(observation);
    if (!selectedProgram) {
        errors.push("Program selection is missing a selected program value.");
    }
    const reasonCodes = resolveReasonCodes(observation);
    if (reasonCodes.length === 0) {
        errors.push("Program selection requires at least one justification reason.");
    }
    if (selectedProgram === "triage") {
        const selectedNeeds = resolveSelectedNeeds(observation);
        if (selectedNeeds.length === 0) {
            errors.push("Triage program selection requires at least one service need.");
        }
    }
    return {
        isValid: errors.length === 0,
        errors
    };
}

/**
 * Observation Validator. Pure functions that validate ObservationFormData before processing.
 * @agentic @benchmark/core
 */ /** Validates observation form data. @agentic @benchmark/core */ const validateObservationFormData = (formData)=>{
    const errors = [];
    // Required fields
    if (!formData["observation-type"]) {
        errors.push("Observation type is required");
    }
    if (formData.value === undefined || formData.value === null) {
        errors.push("Observation value is required");
    }
    return errors;
};

/**
 * Questionnaire Response Validator. Pure functions that validate QuestionnaireResponseFormData before processing.
 * @agentic @benchmark/core
 */ /** Validates questionnaire response form data. @agentic @benchmark/core */ const validateQuestionnaireResponseFormData = (formData)=>{
    const errors = [];
    // Required fields
    if (!formData.questionnaire) {
        errors.push("Questionnaire reference is required");
    }
    if (!formData.status) {
        errors.push("Response status is required");
    }
    return errors;
};

export { checkHealthcareServiceConformance, mapConformanceIssuesToFields, validateCarePlanActivation, validateCarePlanCancellation, validateCarePlanCompletion, validateCarePlanTypeTransition, validateCoCMCarePlanCreation, validateIntakeCompletionGate, validateObservationFormData, validateQuestionnaireResponseFormData, validateTriageCarePlanCreation };
